import { StepProps } from '@betterment/js-runtime';
import { SambaAutoLink } from '@betterment/shared-components';
import { Button, CenteredWorkflowLayout, HR, Label, ListRow } from '@betterment/style-closet-react';
import { Form, Formik } from 'formik';

import { DepositValues } from './data';

import { AmountSchema } from '../Checking/amount-schema';
import { useAccount } from '../Checking/components/AccountProvider';
import { Customer } from '../Checking/data';
import { InputGroupWithLabelAndError } from '../Checking/form-helpers';
import { intoBettermentAccount } from '../Checking/models/account';
import { intoBankAccount } from '../Checking/models/external-account';
import { externalAccountCreationPath } from '../Checking/path-helpers';
import { trackClick } from '../features/track';
import { AccountOption } from '../Shared/components/AccountOption';
import { AccountSuspendedBanner } from '../Shared/components/AccountSuspendedBanner';
import Footer from '../Shared/components/Footer';
import { FormikNumberInput } from '../Shared/components/NumberInput';
import { WorkflowCard } from '../Shared/components-v2/workflow-card/WorkflowCard';

interface Props extends StepProps<DepositValues> {
  customer: Customer;
}

const DepositFlowAmount: React.FC<React.PropsWithChildren<Props>> = ({ customer, flowState, goToNextStep }) => {
  const { account } = useAccount();

  if (customer.externalAccount == null) return null;

  const isAccountSuspended = customer.isSuspended;
  const isExternalAccountLocked = customer.externalAccount?.isLocked ?? false;

  return (
    <CenteredWorkflowLayout heading="Set up deposit">
      <CenteredWorkflowLayout.Slot name="content">
        {isAccountSuspended && <AccountSuspendedBanner />}
        <Formik
          initialValues={flowState.values}
          onSubmit={goToNextStep}
          validationSchema={AmountSchema}
          validateOnChange={false}
          validateOnBlur={false}
        >
          <Form>
            <WorkflowCard variant="compact" css={{ marginBottom: 'space.300' }}>
              <ListRow>
                <ListRow.Slot name="leading">
                  <Label>From</Label>
                  <AccountOption account={intoBankAccount(customer.externalAccount)} />
                </ListRow.Slot>
                <ListRow.Slot name="trailing">
                  <SambaAutoLink
                    to={externalAccountCreationPath(account.id, 'deposit')}
                    title="Choose a different external account"
                    onClick={() => trackClick({ name: 'EditBank', flow: 'DepositToChecking' })}
                  >
                    Change
                  </SambaAutoLink>
                </ListRow.Slot>
              </ListRow>
              <HR variant="list-row" />
              <ListRow>
                <ListRow.Slot name="leading">
                  <Label>To</Label>
                  <AccountOption account={intoBettermentAccount(account, customer)} />
                </ListRow.Slot>
              </ListRow>
            </WorkflowCard>
            <HR variant="workflow-card" />
            <WorkflowCard>
              <InputGroupWithLabelAndError fieldName="amount" labelText="Amount">
                {(fieldProps: { id: string; name: string; mb: string }) => (
                  <FormikNumberInput
                    {...fieldProps}
                    unit="dollars"
                    autoFocus={true}
                    placeholder="0"
                    disabled={isExternalAccountLocked}
                  />
                )}
              </InputGroupWithLabelAndError>
            </WorkflowCard>
            <CenteredWorkflowLayout.Actions>
              <Button type="submit" disabled={isExternalAccountLocked || isAccountSuspended}>
                Review deposit
              </Button>
            </CenteredWorkflowLayout.Actions>
          </Form>
        </Formik>
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot name="footer">
        <Footer />
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};

export default DepositFlowAmount;
